// Shared style/meta maps for Isaiah's Command Center cosmetics.
// Used by the Header avatar, Profile showcase, Dashboard banner, and Arcade.

export const GLOW_COLOR = {
  none: null,
  gold: "#FCD34D",
  rainbow: "#F472B6",
  blue: "#60A5FA",
  green: "#34D399",
  fire: "#F97316",
  ice: "#67E8F9",
  royal: "#A78BFA",
  emerald: "#10B981",
};

export const FRAME_COLOR = {
  none: null,
  gold: "#FCD34D",
  diamond: "#A5F3FC",
  rainbow: "#F472B6",
  fire: "#FB923C",
  royal: "#C084FC",
  cosmic: "#818CF8",
};

export const THEME_GRADIENT = {
  cosmic: "from-indigo-600 via-fuchsia-600 to-purple-700",
  ocean: "from-cyan-500 via-sky-600 to-blue-700",
  forest: "from-emerald-500 via-green-600 to-green-800",
  sunset: "from-orange-400 via-rose-500 to-fuchsia-600",
  neon: "from-fuchsia-500 via-cyan-400 to-lime-400",
  galaxy: "from-violet-700 via-indigo-700 to-slate-900",
  volcano: "from-red-600 via-orange-600 to-amber-500",
  aurora: "from-teal-400 via-green-400 to-purple-500",
  candy: "from-pink-400 via-rose-400 to-rose-500",
  midnight: "from-slate-800 via-indigo-900 to-indigo-950",
};

export const THEME_EMOJI = {
  cosmic: "🌌", ocean: "🌊", forest: "🌲", sunset: "🌅", neon: "⚡",
  galaxy: "🌠", volcano: "🌋", aurora: "🌈", candy: "🍭", midnight: "🌙",
};

export const BADGE_META = {
  none: null,
  founder: { emoji: "👑", label: "Founder" },
  "million-star": { emoji: "🌟", label: "Million-Star" },
  champion: { emoji: "🏆", label: "Champion" },
  genius: { emoji: "🧠", label: "Genius" },
  explorer: { emoji: "🧭", label: "Explorer" },
  legend: { emoji: "🐉", label: "Legend" },
};

export const BOOST_META = {
  none: { emoji: "🚫", label: "No Boost", desc: "Play the normal way." },
  "star-trail": { emoji: "✨", label: "Star Trail", desc: "Sparkles follow your moves." },
  "hype-crowd": { emoji: "🎉", label: "Hype Crowd", desc: "A cheering crowd celebrates wins." },
  "chill-mode": { emoji: "😎", label: "Chill Mode", desc: "A calmer, no-pressure vibe." },
  "mega-popups": { emoji: "💥", label: "Mega Popups", desc: "Extra-big score popups." },
  "rainbow-board": { emoji: "🌈", label: "Rainbow Board", desc: "A colorful rainbow game board." },
};

// Build an inline style for an avatar circle given the equipped glow + frame.
export function avatarAura(glow, frame) {
  const g = GLOW_COLOR[glow];
  const f = FRAME_COLOR[frame];
  const layers = [];
  if (f) layers.push(`0 0 0 3px ${f}`);
  if (g) layers.push(`0 0 14px 3px ${g}`);
  return layers.length ? { boxShadow: layers.join(", ") } : {};
}

// God-mode screen effects.
export const EFFECT_META = {
  "star-rain": { emoji: "🌟", label: "Star Rain", fall: "⭐" },
  "fireworks": { emoji: "🎆", label: "Fireworks", fall: "🎆" },
  "confetti": { emoji: "🎊", label: "Confetti", fall: "🎊" },
  "rainbow-burst": { emoji: "🌈", label: "Rainbow Burst", fall: "🌈" },
  "star-explosion": { emoji: "💥", label: "Star Explosion", fall: "💥" },
  "snow": { emoji: "❄️", label: "Snow", fall: "❄️" },
  "hearts": { emoji: "💖", label: "Hearts", fall: "💖" },
  "money-rain": { emoji: "💰", label: "Money Rain", fall: "💵" },
  "fireball": { emoji: "🔥", label: "Fireballs", fall: "🔥" },
  "lightning": { emoji: "⚡", label: "Lightning Storm", fall: "⚡" },
  "bubbles": { emoji: "🫧", label: "Bubbles", fall: "🫧" },
};

// Sidekick voice choices (OpenAI TTS voices), grouped by feel.
export const VOICE_OPTIONS = [
  { key: "coral", label: "Coral", gender: "female", desc: "Warm & friendly" },
  { key: "nova", label: "Nova", gender: "female", desc: "Bright & excited" },
  { key: "shimmer", label: "Shimmer", gender: "female", desc: "Soft & cheerful" },
  { key: "onyx", label: "Onyx", gender: "male", desc: "Deep & bold" },
  { key: "echo", label: "Echo", gender: "male", desc: "Smooth & calm" },
  { key: "ash", label: "Ash", gender: "male", desc: "Clear & friendly" },
];

export const SIZE_SCALE = { normal: 1, giant: 1.55, tiny: 0.62 };

// Cards shown in the Command Center "God-Mode Powers" grid.
export const POWER_CARDS = [
  { action: "god_mode", params: { on: true }, emoji: "🦸", label: "GOD MODE", desc: "Turn on all your powers!", tint: "fuchsia" },
  { action: "play_effect", params: { effect: "star-rain" }, emoji: "🌟", label: "Make it Rain Stars", desc: "Stars pour down your screen", tint: "amber" },
  { action: "play_effect", params: { effect: "money-rain" }, emoji: "💰", label: "Make it Rain Money", desc: "Cha-ching!", tint: "green" },
  { action: "play_effect", params: { effect: "fireworks" }, emoji: "🎆", label: "Fireworks", desc: "Big booms of light", tint: "sky" },
  { action: "play_effect", params: { effect: "fireball" }, emoji: "🔥", label: "Make it Rain Fireballs", desc: "Blazing fireballs pour down", tint: "orange" },
  { action: "play_effect", params: { effect: "lightning" }, emoji: "⚡", label: "Lightning Storm", desc: "Thunder cracks across the sky", tint: "sky" },
  { action: "play_effect", params: { effect: "bubbles" }, emoji: "🫧", label: "Bubble Storm", desc: "Floaty bubbles everywhere", tint: "cyan" },
  { action: "toggle_fly", params: { on: true }, emoji: "🕊️", label: "Fly Mode", desc: "Make your avatar float", tint: "cyan" },
  { action: "set_size", params: { size: "giant" }, emoji: "🦕", label: "Giant Mode", desc: "Grow huge!", tint: "violet" },
  { action: "set_size", params: { size: "tiny" }, emoji: "🐜", label: "Tiny Mode", desc: "Shrink down!", tint: "violet" },
  { action: "set_size", params: { size: "normal" }, emoji: "🙂", label: "Normal Size", desc: "Back to normal", tint: "slate" },
  { action: "toggle_throne", params: { on: true }, emoji: "👑", label: "Founder Throne", desc: "Rule your world", tint: "amber" },
];

